// @ai-generated 2026-08-13T07:38:21+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import {
    FlexRow,
    NumberStepper,
    type NumberStepperSource,
    useToasts,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useCallback } from 'react';
import styles from './ContainerActions.module.css';
import { type PlannedContainerGroup } from './containerActionGroups';

interface ContainerActionQuantityCellProps {
    group: PlannedContainerGroup;
    /** Whether the minus may remove rows; deleting a child is guarded with Delete. */
    canDelete: boolean;
    onDelete: (actionIds: string[]) => void;
    /** Plans as many more rows like the group's rows as are asked for. Without it the plus stays locked. */
    onGrow?: (group: PlannedContainerGroup, count: number) => void;
    /** Whether a row is being planned, which keeps another from being asked for. */
    growLocked?: boolean;
    /** How many containers of the type stand at the site; the count of a pickup does not go beyond. */
    max?: number;
}

/**
 * The number of rows in a group, as a stepper.
 *
 * The count is not stored anywhere — it is the number of rows — so a reduction is a batch of deletes and
 * a higher count has the server plan the missing rows like the group's in one request.
 */
export function ContainerActionQuantityCell({
    group,
    canDelete,
    onDelete,
    onGrow,
    growLocked = false,
    max,
}: ContainerActionQuantityCellProps) {
    const i18n = useTranslate('');
    const { pushToast } = useToasts();

    const canReduce = canDelete && group.removableActions.length > 0;
    const reduceHint = canDelete
        ? i18n('Alle Containeraktionen sind bestätigt und können nicht entfernt werden.')
        : i18n('Zum Entfernen von Containern fehlt die Berechtigung.');

    const growHint = i18n('Zum Planen weiterer Container fehlt die Berechtigung.');
    const isFull = max !== undefined && group.quantity >= max;
    const cannotGrow = onGrow === undefined || growLocked || isFull;

    const applyTarget = useCallback(
        (target: number, source: NumberStepperSource) => {
            // An emptied field arrives as a zero, indistinguishable from a typed one, and guessing
            // wrong deletes rows. Emptying the group goes through the minus button.
            if (source === 'input' && target < 1) {
                return;
            }

            if (target > group.quantity) {
                if (onGrow !== undefined && !cannotGrow) {
                    onGrow(group, Math.min(target, max ?? target) - group.quantity);
                }
                return;
            }

            if (target === group.quantity) {
                return;
            }

            if (!canReduce) {
                pushToast({ message: reduceHint, variant: 'info' });
                return;
            }

            const requested = group.quantity - target;
            const removable = group.removableActions.length;

            if (removable < requested) {
                pushToast({
                    message: i18n(
                        `Nur ${removable} von ${requested} Containeraktionen wurden entfernt, die übrigen sind bestätigt.`,
                    ),
                    variant: 'info',
                });
            }

            onDelete(group.removableActions.slice(0, requested).map((action) => action.id));
        },
        [canReduce, cannotGrow, group, i18n, max, onDelete, onGrow, pushToast, reduceHint],
    );

    // Names itself, because a bare number sits in the column a number uses on an individual line.
    if (!canDelete) {
        return (
            <span className={styles.readOnlyValue}>
                {i18n('Anzahl')} {group.quantity}
            </span>
        );
    }

    // The floor is zero rather than one: stepping down from a single row is what empties the group,
    // which is the only way out for a type nobody tracks per container.
    return (
        <FlexRow gap="xs" align="center">
            <NumberStepper
                name={`container_quantity_${group.key}`}
                ariaLabel={i18n('Anzahl')}
                value={group.quantity}
                min={0}
                max={cannotGrow ? group.quantity : max}
                editable
                size="xs"
                decrement={{
                    label: i18n('Einen Container weniger'),
                    locked: !canReduce,
                    tooltip: canReduce ? undefined : reduceHint,
                }}
                increment={{
                    label: i18n('Einen Container mehr'),
                    locked: cannotGrow,
                    tooltip: onGrow === undefined ? growHint : undefined,
                }}
                onChange={applyTarget}
            />
            {max !== undefined && (
                <FlexRow className={styles.ofStanding} shrink={false}>{`${i18n('von')} ${max}`}</FlexRow>
            )}
        </FlexRow>
    );
}
